import React from "react";
import { supabase } from "../../clients/supabaseClient";
import { Link } from "react-router-dom";
import ProductForm from "../inventory/components/ProductForm";
import { importProductsFromCSV } from "../../lib/importProducts.ts";
import {
  PlusIcon,
  PencilSquareIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ArrowUpTrayIcon,
  PhotoIcon
} from "@heroicons/react/24/outline";
import { importProductImages } from "../../lib/importImages.ts";

type Product = {
  id: string;
  sku: string;
  name: string;
  is_active: boolean;
  store_name: string;
  created_at: string;
  description?: string | null;
  specification?: string | null;
  image_url?: string | null;
  keywords?: string[] | null;
  products_store?: { stock: number; store_id: string }[];
  product_prices?: { price: number; store_id: string }[];
  stock: number;
  display_store_id?: string | null;
  display_price?: number;
  store_id?: string | null;
};

const ITEMS_PER_PAGE = 100;

// Matches the shape of csvImportResults state below. Named separately so it
// can be reused as the expected return type from importProductsFromCSV.
interface CSVImportResult {
  created: number;
  updated: number;
  skipped: number;
  unchanged: number;
  failed: number;
  errors: string[];
}

export default function ProductsPage() {
  const [items, setItems] = React.useState<Product[]>([]);
  const [q, setQ] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [showForm, setShowForm] = React.useState(false);
  const [editItem, setEditItem] = React.useState<Product | null>(null);
  const [statusFilter, setStatusFilter] = React.useState<"All" | "Active" | "Inactive">("Active");
  const [imageFilter, setImageFilter] = React.useState<"All" | "WithImages" | "NoImages">("All");
  const [specialFilter, setSpecialFilter] = React.useState<"All" | "Special" | "Standard">("All");
  const [storeFilter, setStoreFilter] = React.useState<string>("All");
  const [isFilterOpen, setIsFilterOpen] = React.useState(false);

  const [page, setPage] = React.useState(1);
  const [totalCount, setTotalCount] = React.useState(0);
  const [importing, setImporting] = React.useState(false);
  const [importProgress, setImportProgress] = React.useState(0);
  const [storeList, setStoreList] = React.useState<{ id: string; name: string }[]>([]);
  const [importStoreId, setImportStoreId] = React.useState<string>("");
  const [imageImporting, setImageImporting] = React.useState(false);
  const [imageProgress, setImageProgress] = React.useState(0);
  const [showImageModal, setShowImageModal] = React.useState(false);
  const [stagedFiles, setStagedFiles] = React.useState<File[]>([]);
  const [importResults, setImportResults] = React.useState<{
    success: number;
    failed: number;
    errors: string[];
  } | null>(null);

  const fetchProducts = React.useCallback(async () => {
    setLoading(true);
    const from = (page - 1) * ITEMS_PER_PAGE;
    const to = from + ITEMS_PER_PAGE - 1;

    let query = supabase
        .from("product_inventory_view")
        .select("*", { count: "exact" });

    if (q.trim()) {
      const cleanQ = q.trim().replace(/\s+/g, " ");
      const searchPattern = `%${cleanQ}%`;

      query = query.or(
          `name.ilike.${searchPattern},sku.ilike.${searchPattern},keywords.cs.{"${cleanQ}"}`
      );
    }

    if (statusFilter === "Active") {
      query = query.eq("is_active", true);
    } else if (statusFilter === "Inactive") {
      query = query.eq("is_active", false);
    }

    if (imageFilter === "WithImages") {
      query = query.not("image_url", "is", null);
    } else if (imageFilter === "NoImages") {
      query = query.is("image_url", null);
    }

    if (specialFilter === "Special") {
      query = query.eq("is_special", true);
    } else if (specialFilter === "Standard") {
      query = query.or("is_special.eq.false,is_special.is.null");
    }
    if (storeFilter !== "All") {
      query = query.eq("store_id", storeFilter);
    }

    const { data, error, count } = await query
        .order("created_at", { ascending: false })
        .range(from, to);

    if (error) {
      console.error("❌ View Fetch Error:", error);
      setItems([]);
    } else {
      setItems((data || []) as Product[]);
      setTotalCount(count ?? 0);
    }

    setLoading(false);
  }, [page, q, statusFilter, imageFilter, specialFilter, storeFilter]);

  React.useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  React.useEffect(() => {
    const loadStores = async () => {
      const { data, error } = await supabase.from("stores").select("id,name").order("name");
      if (!error) setStoreList(data ?? []);
    };
    loadStores();
  }, []);

  const [showCSVModal, setShowCSVModal] = React.useState(false);
  const [stagedCSVFile, setStagedCSVFile] = React.useState<File | null>(null);
  const [csvImportResults, setCsvImportResults] = React.useState<CSVImportResult | null>(null);

  const handleCSVUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!importStoreId) {
      alert("Please select a store for this import first.");
      event.currentTarget.value = "";
      return;
    }
    setStagedCSVFile(file);
    setCsvImportResults(null);
    setShowCSVModal(true);
    event.target.value = "";
  };

  const executeCSVImport = async () => {
    if (!stagedCSVFile || !importStoreId) return;
    setImporting(true);
    setImportProgress(0);
    const result = await importProductsFromCSV(stagedCSVFile, importStoreId, (progress) =>
        setImportProgress(progress)
    );
    setCsvImportResults(result as CSVImportResult);
    setImporting(false);
    fetchProducts();
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    setStagedFiles(Array.from(files));
    setImportResults(null);
    setShowImageModal(true);
    event.target.value = "";
  };

  const executeImageImport = async () => {
    setImageImporting(true);
    setImageProgress(0);
    const result = await importProductImages(stagedFiles, (progress) =>
        setImageProgress(progress)
    );
    setImportResults(result);
    setImageImporting(false);
    fetchProducts();
  };

  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  const toggleActive = async (p: Product) => {
    const { error } = await supabase
        .from("products")
        .update({ is_active: !p.is_active })
        .eq("id", p.id);
    if (!error) fetchProducts();
  };

  const resetFilters = () => {
    setStatusFilter("All");
    setImageFilter("All");
    setSpecialFilter("All");
    setStoreFilter("All");
    setQ("");
    setPage(1);
  };

  const formatPrice = (cents: number) => {
    return new Intl.NumberFormat("en-ZA", {
      style: "currency",
      currency: "ZAR",
    }).format(cents / 100);
  };

  return (
      <>
        <div className="space-y-6">
          <div className="flex flex-col space-y-6">
            <div>
              <h1 className="text-2xl font-semibold text-stone-900">Products</h1>
              <p className="text-sm text-stone-500 mt-1">Manage and track all product inventory across stores</p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 items-center w-full justify-between">
              <div className="relative inline-block text-left">
                <button
                    onClick={() => setIsFilterOpen(!isFilterOpen)}
                    className="flex items-center gap-2 px-4 py-2.5 border border-stone-300 rounded-lg bg-white text-sm text-stone-700 shadow-sm hover:bg-stone-50 focus:outline-none focus:ring-2 focus:ring-[#e6186c]"
                >
                  <span className="font-medium">Filters</span>
                  {(statusFilter !== "All" || imageFilter !== "All" || specialFilter !== "All" || storeFilter !== "All") && (
                      <span className="flex h-2 w-2 rounded-full bg-[#e6186c]"></span>
                  )}
                  <svg className="h-4 w-4 text-stone-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {isFilterOpen && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setIsFilterOpen(false)}></div>
                      <div className="absolute left-0 mt-2 w-64 z-20 origin-top-left rounded-xl border border-stone-200 bg-white shadow-xl p-4 space-y-4">
                        <div>
                          <p className="text-[10px] font-bold uppercase tracking-wider text-stone-400 mb-2">Availability</p>
                          <div className="space-y-2">
                            {(["All", "Active", "Inactive"] as const).map((opt) => (
                                <label key={opt} className="flex items-center gap-2 cursor-pointer group">
                                  <input
                                      type="radio"
                                      name="status"
                                      checked={statusFilter === opt}
                                      onChange={() => setStatusFilter(opt)}
                                      className="h-4 w-4 border-stone-300 text-[#e6186c] accent-[#e6186c]"
                                  />
                                  <span className="text-sm text-stone-600 group-hover:text-stone-900">{opt}</span>
                                </label>
                            ))}
                          </div>
                        </div>
                        <div className="h-px bg-stone-100" />
                        <div>
                          <p className="text-[10px] font-bold uppercase tracking-wider text-stone-400 mb-2">Media</p>
                          <div className="space-y-2">
                            {(
                                [
                                  { label: "All", value: "All" },
                                  { label: "With Images", value: "WithImages" },
                                  { label: "Missing Images", value: "NoImages" },
                                ] as const
                            ).map((opt) => (
                                <label key={opt.value} className="flex items-center gap-2 cursor-pointer group">
                                  <input
                                      type="radio"
                                      name="image"
                                      checked={imageFilter === opt.value}
                                      onChange={() => setImageFilter(opt.value)}
                                      className="h-4 w-4 border-stone-300 text-[#e6186c] accent-[#e6186c]"
                                  />
                                  <span className="text-sm text-stone-600 group-hover:text-stone-900">{opt.label}</span>
                                </label>
                            ))}
                          </div>
                        </div>
                        <div className="h-px bg-stone-100" />

                        <div>
                          <p className="text-[10px] font-bold uppercase tracking-wider text-stone-400 mb-2">Store</p>
                          <div className="space-y-2">
                            <label className="flex items-center gap-2 cursor-pointer group">
                              <input
                                  type="radio"
                                  name="store"
                                  checked={storeFilter === "All"}
                                  onChange={() => setStoreFilter("All")}
                                  className="h-4 w-4 border-stone-300 text-[#e6186c] accent-[#e6186c]"
                              />
                              <span className="text-sm text-stone-600 group-hover:text-stone-900">All Stores</span>
                            </label>
                            {storeList.map((store) => (
                                <label key={store.id} className="flex items-center gap-2 cursor-pointer group">
                                  <input
                                      type="radio"
                                      name="store"
                                      checked={storeFilter === store.id}
                                      onChange={() => setStoreFilter(store.id)}
                                      className="h-4 w-4 border-stone-300 text-[#e6186c] accent-[#e6186c]"
                                  />
                                  <span className="text-sm text-stone-600 group-hover:text-stone-900">{store.name}</span>
                                </label>
                            ))}
                          </div>
                        </div>
                        <div className="h-px bg-stone-100" />
                        <div>
                          <p className="text-[10px] font-bold uppercase tracking-wider text-stone-400 mb-2">Promotions</p>
                          <div className="space-y-2">
                            {(
                                [
                                  { label: "All Products", value: "All" },
                                  { label: "Specials Only", value: "Special" },
                                  { label: "Standard Only", value: "Standard" },
                                ] as const
                            ).map((opt) => (
                                <label key={opt.value} className="flex items-center gap-2 cursor-pointer group">
                                  <input
                                      type="radio"
                                      name="special"
                                      checked={specialFilter === opt.value}
                                      onChange={() => setSpecialFilter(opt.value)}
                                      className="h-4 w-4 border-stone-300 text-[#e6186c] accent-[#e6186c]"
                                  />
                                  <span className="text-sm text-stone-600 group-hover:text-stone-900">{opt.label}</span>
                                </label>
                            ))}
                          </div>
                        </div>
                        <div className="pt-2 border-t border-stone-100">
                          <button
                              onClick={() => {
                                resetFilters();
                                setIsFilterOpen(false);
                              }}
                              className="w-full text-center py-2 text-xs font-semibold text-stone-400 hover:text-[#e6186c]"
                          >
                            Clear All Filters
                          </button>
                        </div>
                      </div>
                    </>
                )}
              </div>

              <div className="flex-1 min-w-[300px] relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
                <input
                    value={q}
                    onChange={(e) => {
                      setQ(e.target.value);
                      setPage(1);
                    }}
                    placeholder="Search products, SKU, keywords, category…"
                    className="w-full border border-stone-300 rounded-lg pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#e6186c] bg-white shadow-sm"
                />
              </div>

              <div className="relative inline-block">
                <select
                    value={importStoreId}
                    onChange={(e) => setImportStoreId(e.target.value)}
                    className="block w-full appearance-none pr-10 border border-stone-300 rounded-lg bg-white px-4 py-2.5 text-sm text-stone-700 shadow-sm focus:ring-2 focus:ring-[#e6186c]"
                >
                  <option value="">Store for import…</option>
                  {storeList.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>

              <label
                  className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-stone-300 text-stone-700 text-sm font-medium transition-colors cursor-pointer bg-white hover:bg-stone-50 whitespace-nowrap ${
                      importing || !importStoreId ? "opacity-50 cursor-not-allowed" : ""
                  }`}
              >
                <ArrowUpTrayIcon className="h-4 w-4" />
                {importing ? "Importing..." : "Import CSV"}
                <input
                    type="file"
                    accept=".csv"
                    onChange={handleCSVUpload}
                    className="hidden"
                    disabled={importing || !importStoreId}
                />
              </label>

              <label
                  className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-stone-300 text-stone-700 text-sm font-medium transition-colors cursor-pointer bg-white hover:bg-stone-50 whitespace-nowrap ${
                      imageImporting ? "opacity-50 cursor-not-allowed" : ""
                  }`}
              >
                <PhotoIcon className="h-4 w-4" />
                {imageImporting ? `Uploading ${imageProgress}%` : "Import Images"}
                <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    className="hidden"
                    disabled={imageImporting}
                />
              </label>

              <button
                  onClick={() => {
                    setEditItem(null);
                    setShowForm(true);
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#e6186c] text-white text-sm font-semibold hover:bg-[#d01560] transition-colors whitespace-nowrap shadow-sm"
              >
                <PlusIcon className="h-4 w-4" />
                Add Product
              </button>
            </div>
          </div>

          <div className="rounded-2xl border border-stone-200 overflow-hidden shadow-sm bg-white">
            <div className="h-1 bg-[#e6186c] w-full" />
            <table className="min-w-full text-sm">
              <thead className="bg-stone-50/60 border-b border-stone-200 text-stone-600">
              <tr className="text-left">
                <th className="py-3 px-4 font-semibold text-xs uppercase tracking-tight">Image</th>
                <th className="py-3 px-4 font-semibold text-xs uppercase tracking-tight">SKU</th>
                <th className="py-3 px-4 font-semibold text-xs uppercase tracking-tight">Name</th>
                <th className="py-3 px-4 font-semibold text-xs uppercase tracking-tight">Store</th>
                <th className="py-3 px-4 font-semibold text-xs uppercase tracking-tight">Price</th>
                <th className="py-3 px-4 font-semibold text-xs uppercase tracking-tight">Stock</th>
                <th className="py-3 px-4 font-semibold text-xs uppercase tracking-tight">Status</th>
                <th className="py-3 px-4 font-semibold text-xs uppercase tracking-tight">Actions</th>
              </tr>
              </thead>
              <tbody>
              {loading ? (
                  <tr>
                    <td className="py-12 text-center text-stone-400" colSpan={8}>
                      Loading products…
                    </td>
                  </tr>
              ) : items.length === 0 ? (
                  <tr>
                    <td className="py-12 text-center text-stone-400" colSpan={8}>
                      No products found
                    </td>
                  </tr>
              ) : (
                  items.map((p, idx) => (
                      <tr
                          key={`${p.id}-${p.display_store_id}-${idx}`}
                          className="border-b border-stone-100 last:border-0 hover:bg-stone-50/40 transition-colors"
                      >
                        <td className="py-4 px-4">
                          {p.image_url ? (
                              <img src={p.image_url} alt={p.name} className="w-10 h-10 object-cover rounded-lg border border-stone-200" />
                          ) : (
                              <div className="w-10 h-10 rounded-lg bg-stone-100 border border-stone-200 flex items-center justify-center">
                                <span className="text-xs text-gray-400">—</span>
                              </div>
                          )}
                        </td>
                        <td className="py-4 px-4 font-mono text-stone-700">{p.sku}</td>
                        <td className="py-4 px-4">
                          <div className="font-medium text-stone-900">{p.name}</div>
                          {p.keywords?.length ? (
                              <div className="mt-1 flex flex-wrap gap-1">
                                {p.keywords.slice(0, 4).map((kw) => (
                                    <span
                                        key={kw}
                                        className="inline-flex rounded-md bg-stone-100 px-2 py-0.5 text-[10px] font-medium text-stone-600"
                                    >
                                                        {kw}
                                                    </span>
                                ))}
                              </div>
                          ) : null}
                        </td>
                        <td className="py-4 px-4 text-stone-600">
                                        <span className="inline-flex px-2 py-0.5 rounded-md bg-stone-100 text-[11px] font-bold text-stone-500 uppercase tracking-tight">
                                            {p.store_name ?? "No Store"}
                                        </span>
                        </td>
                        <td className="py-4 px-4 font-medium text-stone-900">{formatPrice(p.display_price ?? 0)}</td>
                        <td className="py-4 px-4 text-stone-600 font-semibold">{p.stock ?? 0}</td>
                        <td className="py-4 px-4">
                                        <span
                                            className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ring-1 ${
                                                p.is_active
                                                    ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                                                    : "bg-stone-100 text-stone-600 ring-stone-200"
                                            }`}
                                        >
                                            {p.is_active ? "Active" : "Inactive"}
                                        </span>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-1">
                            <button
                                onClick={() => {
                                  setEditItem(p);
                                  setShowForm(true);
                                }}
                                className="p-2 rounded-lg text-stone-600 hover:bg-stone-100 hover:text-stone-900 transition-colors"
                            >
                              <PencilSquareIcon className="h-4 w-4" />
                            </button>
                            <Link
                                to={`/products/${p.id}/${p.store_id}`}
                                className="p-2 rounded-lg text-stone-600 hover:bg-stone-100 hover:text-stone-900 transition-colors"
                            >
                              <ChevronRightIcon className="h-4 w-4" />
                            </Link>
                            <button
                                onClick={() => toggleActive(p)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                    p.is_active
                                        ? "text-stone-600 hover:bg-red-50 hover:text-red-700"
                                        : "text-stone-600 hover:bg-emerald-50 hover:text-emerald-700"
                                }`}
                            >
                              {p.is_active ? "Disable" : "Enable"}
                            </button>
                          </div>
                        </td>
                      </tr>
                  ))
              )}
              </tbody>
            </table>

            {!loading && items.length > 0 && (
                <div className="px-4 py-4 border-t border-stone-200 bg-stone-50/30 flex items-center justify-between">
                  <div className="text-xs text-stone-500">
                    Showing <span className="font-medium text-stone-900">{(page - 1) * ITEMS_PER_PAGE + 1}</span> to{" "}
                    <span className="font-medium text-stone-900">{Math.min(page * ITEMS_PER_PAGE, totalCount)}</span> of{" "}
                    <span className="font-medium text-stone-900">{totalCount}</span> products
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                        disabled={page === 1}
                        onClick={() => setPage((p) => p - 1)}
                        className="p-2 rounded-lg border border-stone-300 bg-white hover:bg-stone-50 disabled:opacity-40 transition-colors shadow-sm"
                    >
                      <ChevronLeftIcon className="h-4 w-4" />
                    </button>
                    <span className="text-xs font-medium text-stone-600 px-2">
                                    Page {page} of {totalPages}
                                </span>
                    <button
                        disabled={page >= totalPages}
                        onClick={() => setPage((p) => p + 1)}
                        className="p-2 rounded-lg border border-stone-300 bg-white hover:bg-stone-50 disabled:opacity-40 transition-colors shadow-sm"
                    >
                      <ChevronRightIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
            )}
          </div>
        </div>

        {showForm && <ProductForm initial={editItem ?? undefined} onClose={() => setShowForm(false)} onSaved={fetchProducts} />}

        {showCSVModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-stone-900/40 backdrop-blur-sm">
              <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg border border-stone-200 overflow-hidden">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-stone-900 text-center w-full">
                      {csvImportResults ? "Import Summary" : "Confirm Product Import"}
                    </h3>
                    {!importing && (
                        <button onClick={() => setShowCSVModal(false)} className="text-stone-400 hover:text-stone-600 absolute right-6 top-6">
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                    )}
                  </div>

                  {!csvImportResults ? (
                      <div className="space-y-4">
                        <div className="p-4 bg-stone-50 rounded-xl border border-stone-200">
                          <p className="text-sm text-stone-600">
                            File: <span className="font-mono font-bold text-stone-900">{stagedCSVFile?.name}</span>
                          </p>
                          <p className="text-sm text-stone-600 mt-1">
                            Target Store:{" "}
                            <span className="font-bold text-[#e6186c]">
                                                {storeList.find((s) => s.id === importStoreId)?.name}
                                            </span>
                          </p>
                        </div>
                        {importing && (
                            <div className="space-y-2 mt-4">
                              <div className="w-full bg-stone-100 rounded-full h-2">
                                <div className="bg-[#e6186c] h-2 rounded-full transition-all duration-300" style={{ width: `${importProgress}%` }} />
                              </div>
                              <p className="text-xs text-center text-stone-500 font-medium">Processing... {importProgress}%</p>
                            </div>
                        )}
                        <div className="flex gap-3 mt-6">
                          <button
                              disabled={importing}
                              onClick={() => setShowCSVModal(false)}
                              className="flex-1 px-4 py-2.5 border border-stone-300 rounded-lg text-sm font-medium hover:bg-stone-50 disabled:opacity-50"
                          >
                            Cancel
                          </button>
                          <button
                              disabled={importing}
                              onClick={executeCSVImport}
                              className="flex-1 px-4 py-2.5 bg-[#e6186c] text-white rounded-lg text-sm font-medium hover:bg-[#d01560] disabled:opacity-50"
                          >
                            {importing ? "Processing..." : "Start Import"}
                          </button>
                        </div>
                      </div>
                  ) : (
                      <div className="space-y-6">
                        <div className="grid grid-cols-2 gap-3">
                          <div className="bg-emerald-50 p-4 rounded-xl border border-emerald-100 text-center">
                            <p className="text-xl font-bold text-emerald-600">{csvImportResults.created}</p>
                            <p className="text-[10px] font-bold text-emerald-700 uppercase">New</p>
                          </div>
                          <div className="bg-blue-50 p-4 rounded-xl border border-blue-100 text-center">
                            <p className="text-xl font-bold text-blue-600">{csvImportResults.updated}</p>
                            <p className="text-[10px] font-bold text-blue-700 uppercase">Updated</p>
                          </div>
                          <div className="bg-stone-50 p-4 rounded-xl border border-stone-200 text-center">
                            <p className="text-xl font-bold text-stone-600">{csvImportResults.unchanged}</p>
                            <p className="text-[10px] font-bold text-stone-700 uppercase">Unchanged</p>
                          </div>
                          <div className="bg-red-50 p-4 rounded-xl border border-red-100 text-center">
                            <p className="text-xl font-bold text-red-600">{csvImportResults.failed}</p>
                            <p className="text-[10px] font-bold text-red-700 uppercase">Failed</p>
                          </div>
                        </div>
                        {csvImportResults.errors.length > 0 && (
                            <div className="mt-2">
                              <p className="text-xs font-bold text-stone-400 uppercase mb-2">Error Details</p>
                              <div className="max-h-40 overflow-y-auto rounded-xl border border-stone-200 bg-stone-50 p-3">
                                {csvImportResults.errors.map((err, idx) => (
                                    <div key={idx} className="text-[11px] text-red-600 py-1 border-b border-stone-100 last:border-0 flex gap-2">
                                      <span className="font-bold opacity-50">{idx + 1}.</span>
                                      <span>{err}</span>
                                    </div>
                                ))}
                              </div>
                            </div>
                        )}
                        <button
                            onClick={() => {
                              setShowCSVModal(false);
                              setCsvImportResults(null);
                            }}
                            className="w-full px-4 py-3 bg-stone-900 text-white rounded-xl text-sm font-semibold hover:bg-stone-800 shadow-sm"
                        >
                          Finish & Close
                        </button>
                      </div>
                  )}
                </div>
              </div>
            </div>
        )}

        {showImageModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-stone-900/40 backdrop-blur-sm">
              <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden border border-stone-200">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-stone-900">{importResults ? "Import Summary" : "Confirm Image Upload"}</h3>
                    {!imageImporting && (
                        <button onClick={() => setShowImageModal(false)} className="text-stone-400 hover:text-stone-600">
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                    )}
                  </div>
                  {!importResults ? (
                      <div className="space-y-4">
                        <p className="text-sm text-stone-600">
                          You have selected <span className="font-bold text-stone-900">{stagedFiles.length}</span> images. Matching products will have their images replaced.
                        </p>
                        {imageImporting && (
                            <div className="space-y-2">
                              <div className="w-full bg-stone-100 rounded-full h-2">
                                <div className="bg-[#e6186c] h-2 rounded-full transition-all duration-300" style={{ width: `${imageProgress}%` }} />
                              </div>
                              <p className="text-xs text-center text-stone-500 font-medium">Processing... {imageProgress}%</p>
                            </div>
                        )}
                        <div className="flex gap-3 mt-6">
                          <button
                              disabled={imageImporting}
                              onClick={() => setShowImageModal(false)}
                              className="flex-1 px-4 py-2.5 border border-stone-300 rounded-lg text-sm font-medium hover:bg-stone-50 disabled:opacity-50"
                          >
                            Cancel
                          </button>
                          <button
                              disabled={imageImporting}
                              onClick={executeImageImport}
                              className="flex-1 px-4 py-2.5 bg-[#e6186c] text-white rounded-lg text-sm font-medium hover:bg-[#d01560] disabled:opacity-50"
                          >
                            {imageImporting ? "Uploading..." : "Start Upload"}
                          </button>
                        </div>
                      </div>
                  ) : (
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4 py-2">
                          <div className="bg-emerald-50 p-3 rounded-xl border border-emerald-100 text-center">
                            <p className="text-2xl font-bold text-emerald-600">{importResults.success}</p>
                            <p className="text-xs font-medium text-emerald-700 uppercase">Matched</p>
                          </div>
                          <div className="bg-red-50 p-3 rounded-xl border border-red-100 text-center">
                            <p className="text-2xl font-bold text-red-600">{importResults.failed}</p>
                            <p className="text-xs font-medium text-red-700 uppercase">Failed</p>
                          </div>
                        </div>
                        {importResults.errors.length > 0 && (
                            <div className="mt-4">
                              <p className="text-sm font-semibold text-stone-900 mb-2">Error Details:</p>
                              <div className="max-h-48 overflow-y-auto rounded-lg border border-stone-200 bg-stone-50 p-3 space-y-2">
                                {importResults.errors.map((err, idx) => (
                                    <div key={idx} className="text-xs text-red-600 flex gap-2">
                                      <span>•</span>
                                      <span>{err}</span>
                                    </div>
                                ))}
                              </div>
                            </div>
                        )}
                        <button
                            onClick={() => setShowImageModal(false)}
                            className="w-full mt-4 px-4 py-2.5 bg-stone-900 text-white rounded-lg text-sm font-medium hover:bg-stone-800 transition-colors"
                        >
                          Close
                        </button>
                      </div>
                  )}
                </div>
              </div>
            </div>
        )}
      </>
  );
}